import {
  STAFF_CATALOG_SEARCH_BOUNDS as BOUNDS,
  type StaffCatalogItemStatus,
  type StaffCatalogItemType,
} from "@lafayette/schemas";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../ui/cn";
import { CheckboxField, focusFirstInvalid, TextField } from "../../ui/fields";
import { Label } from "../../ui/label";
import {
  checkLabel,
  checkRow,
  fieldError,
  filterPanel,
  groupBox,
  groupLegend,
  hintText,
} from "../components/styles";
import { itemTypeOptions, statusOptions } from "../labels";
import {
  joinCodes,
  SEARCH_RADIUS_KM,
  type SearchFieldKey,
  type SearchFormErrors,
  type SearchFormValues,
  splitCodes,
  validateSearchValues,
} from "./form";

type DietaryChoice = Readonly<{ code: string; label: string }>;

type SearchFormProps = Readonly<{
  initial: SearchFormValues;
  /** Errors of a search that came from the address (a link with values that are not valid). */
  initialErrors: SearchFormErrors;
  busy: boolean;
  /** `null` while the list loads. An empty list means the form takes free codes. */
  dietaryChoices: readonly DietaryChoice[] | null;
  onSubmit: (values: SearchFormValues) => void;
  onClear: () => void;
}>;

const toggled = <T extends string>(list: readonly T[], value: T, on: boolean): readonly T[] =>
  on ? (list.includes(value) ? list : [...list, value]) : list.filter((entry) => entry !== value);

/**
 * The search form. The values are local until Search: then the form checks them, and only valid values
 * reach the address. An error names the field in words, ties to it with `aria-describedby`, and the
 * focus moves to the first invalid control. The form reads and changes no catalog data.
 */
export function SearchForm({
  initial,
  initialErrors,
  busy,
  dietaryChoices,
  onSubmit,
  onClear,
}: SearchFormProps) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<SearchFormErrors>(initialErrors);
  const [focusRequest, setFocusRequest] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (focusRequest > 0) focusFirstInvalid(formRef.current);
  }, [focusRequest]);

  const set =
    <K extends keyof SearchFormValues>(key: K) =>
    (value: SearchFormValues[K]) =>
      setValues((current) => ({ ...current, [key]: value }));
  const err = (key: SearchFieldKey) => errors[key];

  const dietaryCodes = splitCodes(values.dietary);
  const listedCodes = new Set((dietaryChoices ?? []).map((choice) => choice.code));
  // A code from the address that the list does not know stays visible, checked, so it can be unchecked.
  const choices: readonly DietaryChoice[] = [
    ...(dietaryChoices ?? []),
    ...dietaryCodes.filter((code) => !listedCodes.has(code)).map((code) => ({ code, label: code })),
  ];
  const freeCodes = dietaryChoices !== null && dietaryChoices.length === 0;

  const textLength = values.text.length;
  const textTooLong = textLength > BOUNDS.textChars;

  return (
    <form
      ref={formRef}
      className={filterPanel}
      aria-label="Search the catalog"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        const found = validateSearchValues(values);
        setErrors(found);
        if (Object.keys(found).length > 0) setFocusRequest((count) => count + 1);
        else onSubmit(values);
      }}
    >
      <TextField
        id="search-text"
        label="What are you looking for?"
        type="search"
        value={values.text}
        onChange={set("text")}
        error={err("text")}
        hint={`${textTooLong ? "⚠ " : ""}${textLength} / ${BOUNDS.textChars} characters${
          textTooLong ? ". Too long." : ""
        }`}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <fieldset className={groupBox}>
          <legend className={groupLegend}>Item types</legend>
          <p className={hintText}>Leave all unchecked to search every type.</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {itemTypeOptions.map((option) => (
              <CheckboxField
                key={option.value}
                id={`search-type-${option.value}`}
                label={option.label}
                checked={values.types.includes(option.value as StaffCatalogItemType)}
                onChange={(on) =>
                  set("types")(toggled(values.types, option.value as StaffCatalogItemType, on))
                }
              />
            ))}
          </div>
        </fieldset>

        <fieldset
          className={groupBox}
          aria-describedby={err("statuses") ? "search-statuses-error" : "search-statuses-hint"}
        >
          <legend className={groupLegend}>Statuses</legend>
          <p id="search-statuses-hint" className={hintText}>
            Published items only, unless you add more. Staff can look for items that customers
            cannot see.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {statusOptions.map((option, index) => (
              <div className={checkRow} key={option.value}>
                <Checkbox
                  id={`search-status-${option.value}`}
                  name={`search-status-${option.value}`}
                  checked={values.statuses.includes(option.value as StaffCatalogItemStatus)}
                  aria-invalid={err("statuses") && index === 0 ? true : undefined}
                  onChange={(event) =>
                    set("statuses")(
                      toggled(
                        values.statuses,
                        option.value as StaffCatalogItemStatus,
                        event.currentTarget.checked,
                      ),
                    )
                  }
                />
                <Label htmlFor={`search-status-${option.value}`} className={checkLabel}>
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
          {err("statuses") ? (
            <p id="search-statuses-error" className={fieldError}>
              <span aria-hidden="true">⚠ </span>
              {err("statuses")}
            </p>
          ) : null}
        </fieldset>
      </div>
      <div className="grid gap-4 lg:grid-cols-[5fr_6fr]">
        <fieldset className={groupBox}>
          <legend className={groupLegend}>Place</legend>
          <div className="grid gap-4 grid-cols-1 min-[380px]:[grid-template-columns:minmax(min(100%,10rem),1fr)_minmax(min(100%,6rem),8rem)]">
            <TextField
              id="search-locality"
              label="City"
              value={values.locality}
              onChange={set("locality")}
              error={err("locality")}
              hint="The locality of the item, for example Valencia."
            />
            <TextField
              id="search-country"
              label="Country code"
              value={values.countryCode}
              onChange={set("countryCode")}
              error={err("countryCode")}
              hint="Two letters, for example ES."
            />
          </div>
        </fieldset>

        <fieldset className={groupBox}>
          <legend className={groupLegend}>Near a point</legend>
          <p className={hintText}>
            Fill in the latitude, the longitude and the radius together, or leave all three empty.
          </p>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,8rem),1fr))]">
            <TextField
              id="search-latitude"
              label="Latitude"
              inputMode="decimal"
              value={values.latitude}
              onChange={set("latitude")}
              error={err("latitude")}
              hint="From -90 to 90."
            />
            <TextField
              id="search-longitude"
              label="Longitude"
              inputMode="decimal"
              value={values.longitude}
              onChange={set("longitude")}
              error={err("longitude")}
              hint="From -180 to 180."
            />
            <TextField
              id="search-radius"
              label="Radius (km)"
              inputMode="decimal"
              value={values.radiusKm}
              onChange={set("radiusKm")}
              error={err("radiusKm")}
              hint={`From ${SEARCH_RADIUS_KM.min} to ${SEARCH_RADIUS_KM.max}.`}
            />
          </div>
        </fieldset>
      </div>
      <fieldset
        className={groupBox}
        aria-describedby={err("dietary") ? "search-dietary-error" : undefined}
      >
        <legend className={groupLegend}>Dietary tags</legend>
        <p className={hintText}>An item must carry every tag you choose.</p>
        {dietaryChoices === null ? (
          <p className={hintText}>Loading the dietary tags…</p>
        ) : freeCodes ? (
          <TextField
            id="search-dietary"
            label="Dietary codes"
            value={values.dietary}
            onChange={set("dietary")}
            error={err("dietary")}
            hint="The tag list is not available. Type the codes, separated by commas, for example vegan, halal."
          />
        ) : (
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {choices.map((choice, index) => (
              <div className={checkRow} key={choice.code}>
                <Checkbox
                  id={`search-dietary-${index}`}
                  name={`search-dietary-${index}`}
                  checked={dietaryCodes.includes(choice.code)}
                  aria-invalid={err("dietary") && index === 0 ? true : undefined}
                  onChange={(event) =>
                    set("dietary")(
                      joinCodes(toggled(dietaryCodes, choice.code, event.currentTarget.checked)),
                    )
                  }
                />
                <Label htmlFor={`search-dietary-${index}`} className={checkLabel}>
                  {choice.label}
                </Label>
              </div>
            ))}
          </div>
        )}
        {err("dietary") && !freeCodes ? (
          <p id="search-dietary-error" className={fieldError}>
            <span aria-hidden="true">⚠ </span>
            {err("dietary")}
          </p>
        ) : null}
      </fieldset>

      <div className="grid items-start gap-4 sm:grid-cols-[15rem_minmax(0,1fr)]">
        <TextField
          id="search-limit"
          label="How many results"
          inputMode="numeric"
          value={values.limit}
          onChange={set("limit")}
          error={err("limit")}
          hint={`From 1 to ${BOUNDS.limitMax}. The default is ${BOUNDS.limitDefault}.`}
          placeholder={String(BOUNDS.limitDefault)}
        />
        <div className="mt-1 grid min-h-11 grid-cols-[2.25rem_minmax(0,1fr)] items-center gap-x-3 compact:min-h-8 sm:mt-6">
          <Checkbox
            id="search-debug"
            name="search-debug"
            role="switch"
            aria-checked={values.debug}
            checked={values.debug}
            aria-describedby="search-debug-hint"
            onChange={(event) => set("debug")(event.currentTarget.checked)}
          />
          <Label htmlFor="search-debug" className={checkLabel}>
            Show debug scores
          </Label>
          <p id="search-debug-hint" className={cn(hintText, "col-start-2")}>
            Adds the counts of candidates, the vector path and the filters that block a search.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {busy ? "Searching…" : "Search"}
        </Button>
        <Button type="button" variant="secondary" disabled={busy} onClick={onClear}>
          Clear the form
        </Button>
      </div>
    </form>
  );
}
